import { useMemo } from 'react'
import { Menu as MenuAntd } from 'antd'
import { observer } from 'mobx-react-lite'
import { useMenuNavigation } from '@/Core/Hooks/useMenuNavigation'
import { userManager } from '@/Core/Managers'
import { Menu as menuInputs } from '@/Ship/Menu'

import { CheckMenuItemAccessTask } from '@/Ship/Tasks/CheckMenuItemAccessTask'

import styles from './Menu.module.css'

interface MenuNode {
    key?: string
    children?: MenuNode[]
}

/**
 * Прибирає пункти, закриті для цього користувача.
 *
 * Група, з якої зникли всі діти, зникає теж: заголовок без жодного пункту —
 * це обіцянка розділу, якого немає.
 */
const filterByAccess = <T extends MenuNode>(items: T[]): T[] =>
    items
        .map((item) => {
            if (!Array.isArray(item.children)) return item

            return { ...item, children: filterByAccess(item.children) }
        })
        .filter((item) => {
            if (Array.isArray(item.children) && item.children.length === 0) return false

            return CheckMenuItemAccessTask(item.key)
        })

/**
 * Бокове меню.
 *
 * ⚠️ `observer` тут не косметика: права приїжджають окремим запитом уже після
 * першого рендера, і без підписки на `userManager` меню лишалося б таким, яким
 * зібралося до їх завантаження. Для закритого пункту це означало б протилежне
 * задуманому — він **не з'явився б** у того, кому належить.
 */
interface MenuProps {
    /** Після переходу пунктом — мобільна шухляда меню закривається (`SPEC-014` §1.4). */
    onNavigate?: () => void
}

export const Menu = observer(({ onNavigate }: MenuProps) => {
    const memoizedMenuInputs = useMemo(() => menuInputs, [])

    const { menuItems, handleMenuClick, selectedKeys, openKeys, handleOpenChange } =
        useMenuNavigation(memoizedMenuInputs)

    // Читаємо права явно, щоб MobX побачив залежність: `CheckMenuItemAccessTask`
    // дістає їх усередині, і без цього рядка перерахунку після завантаження
    // не сталося б
    const permissions = userManager.user?.['permissions']

    const visibleItems = useMemo(() => filterByAccess(menuItems ?? []), [menuItems, permissions])

    return (
        <div className={styles['menuContainer']}>
            <MenuAntd
                mode="inline"
                selectedKeys={selectedKeys}
                openKeys={openKeys}
                onOpenChange={handleOpenChange}
                items={visibleItems}
                onClick={(info) => {
                    handleMenuClick(info)
                    onNavigate?.()
                }}
            />
        </div>
    )
})
